import React, { useEffect, useMemo, useRef, useState } from 'react';

import * as PropTypes from 'prop-types';

import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { EmptyPage } from 'apps/auth/components/common/Layout/EmptyPage/EmptyPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { PanelContent, PanelFrame, PanelRow } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import { fetchData } from 'apps/auth/helpers/api';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { usePageMessages } from 'apps/auth/hooks/selectors';

import { SearchBar } from './components/SearchBar/SearchBar';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
}

function useDelayForSearch(searchFunction, query, delay = 300) {
    const debouncedQuery = useDebounce(query, delay);

    useEffect(() => {
        if (debouncedQuery) {
            searchFunction(debouncedQuery);
        } else {
            searchFunction(null);
        }
    }, [debouncedQuery]);
}

function useApiSearch(searchRef, props, setItems) {
    const searchWithApi = (term) => {
        if (!!term) {
            let query = term.toString().replaceAll(/\s/g, '');
            fetchData(`/data-by-postcode/${query}`)
                .then((data) => {
                    if (!!data && !data.errorText) {
                        setItems(data);
                    } else {
                        setItems([]);
                    }
                })
                .catch(() => {
                    setItems([]);
                });
        } else {
            setItems([]);
        }
    };

    const selectItem = (e, item) => {
        props.selectItem(e, item);
    };

    return [selectItem, searchWithApi];
}

const getItemView = (item) => ({
    id: item['id'] || '',
    mainText: item['address'] || '',
    city: item['city'] || '',
    streetAndHouseNo: item['addressLine2'] || '',
    apartment: item['addressLine1'] || '',
});

const InputAutoCompletePostCodeList = (props) => {
    const { textNotFound, textInfoNotFound, buttonNotFound } = usePageMessages();

    const searchRef = useRef(null);

    const [itemsList, setItems] = useState(props.items);

    const [query, setQuery] = useState(props.field.value);
    const [selectItem, searchWithApi] = useApiSearch(searchRef, props, setItems);

    const postCode = useMemo(() => {
        return query;
    }, [itemsList, itemsList.length]);

    useDelayForSearch(searchWithApi, query, 300);

    const onChange = (e) => {
        e.preventDefault();
        setQuery(e.target.value);
    };

    const setCurrentValue = (e) => {
        e.persist();
        props.selectInputClient(e, query);
        props.onCloseButtonClick();
    };

    const selectNewItem = (e, item) => {
        e.persist();
        selectItem(e, { ...item, postCode: query });
    };

    const notFoundBlock = (currentValue) => {
        const text = itemsList.length > 0 ? textInfoNotFound : textNotFound;
        return resolveArguments(text, currentValue);
    };

    const renderItems = () => (
        <>
            {itemsList.length > 0 &&
                itemsList.map((item, index) => {
                    const { id, city, apartment, streetAndHouseNo } = getItemView(item);
                    return (
                        <Panel key={`address_list_${index}`} id={`reg-country-${id}`} onClick={(e) => selectNewItem(e, item)} isHoverable negativeMargin>
                            <PanelFrame>
                                <PanelContent>
                                    {apartment && (
                                        <PanelRow>
                                            <Text>{apartment.replaceAll(`, `, ` • `)}</Text>
                                        </PanelRow>
                                    )}
                                    {streetAndHouseNo && (
                                        <PanelRow>
                                            <Text>{streetAndHouseNo}</Text>
                                        </PanelRow>
                                    )}
                                    <PanelRow>
                                        <Text color="secondary" size="secondary">
                                            {`${city} • ${postCode}`}
                                        </Text>
                                    </PanelRow>
                                </PanelContent>
                                {id === props.selectedPostCode.id && <Icon name="check" color="primary" leftIndent="8px" />}
                            </PanelFrame>
                        </Panel>
                    );
                })}
        </>
    );

    return (
        <>
            <SearchBar
                placeholder={props.searchPlaceholder}
                onChange={onChange}
                id={`search_field_${props.field.name}_${props.field.id}`}
                onCloseButtonClick={props.onCloseButtonClick}
                searchRef={searchRef}
                value={query}
            />
            <Row indent="adaptive-32-48">{renderItems()}</Row>
            {(props.selectedPostCode.postCode.length > 0 || query) && (
                <EmptyPage
                    iconName={itemsList.length === 0 ? 'ghost' : ''}
                    emptyText={notFoundBlock(query)}
                    emptyButtonClick={setCurrentValue}
                    emptyButtonCaption={buttonNotFound}
                />
            )}
        </>
    );
};

InputAutoCompletePostCodeList.propTypes = {
    selectItem: PropTypes.func,
    setItems: PropTypes.func,
    setCurrentValue: PropTypes.func,
    selectedPostCode: PropTypes.any,
    selectInputClient: PropTypes.func,
    items: PropTypes.array,
    onCloseButtonClick: PropTypes.func,
};

export default InputAutoCompletePostCodeList;
